import { useEffect, useMemo } from 'react';
import type { FieldValues, Path, PathValue, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { safeStorage } from '@/shared/utils/safeStorage';

interface UseFormDraftOptions<T extends FieldValues> {
  formType: string;
  fields: readonly Path<T>[];
  watch: UseFormWatch<T>;
  setValue: UseFormSetValue<T>;
}

export function useFormDraft<T extends FieldValues>({
  formType,
  fields,
  watch,
  setValue,
}: UseFormDraftOptions<T>) {
  const key = `svq_draft_${formType}`;
  const fieldNames = fields.join(',');
  const draftFields = useMemo(() => fieldNames.split(',') as Path<T>[], [fieldNames]);

  useEffect(() => {
    const restored = safeStorage.readDraft(key);
    if (!restored) return;

    for (const field of draftFields) {
      const value = restored[field];
      if (typeof value === 'string' && value !== '') {
        setValue(field, value as PathValue<T, Path<T>>);
      }
    }
  }, [key, draftFields, setValue]);

  useEffect(() => {
    const subscription = watch((values) => {
      const draft: Record<string, unknown> = {};
      for (const field of draftFields) draft[field] = values[field];

      safeStorage.writeDraft(key, draft);
    });

    return () => subscription.unsubscribe();
  }, [key, draftFields, watch]);

  return { discard: () => safeStorage.discardDraft(key) };
}
